import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../utils/firebase';
import { DynamicSurveyResponse, APIResponse } from '../types/survey.types';
import { retryFirebaseCall } from '../utils/retry';

interface UseResponsesReturn {
  loading: boolean;
  error: string | null;
  submitResponse: (response: DynamicSurveyResponse) => Promise<string>;
  getResponses: (surveyId: string) => Promise<DynamicSurveyResponse[]>;
}

export const useResponses = (): UseResponsesReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Submit a survey response
   */
  const submitResponse = useCallback(async (response: DynamicSurveyResponse): Promise<string> => {
    setLoading(true);
    setError(null);

    try {
      const submitResponseFn = httpsCallable<DynamicSurveyResponse, APIResponse<string>>(
        functions,
        'submitResponse'
      );

      const result = await retryFirebaseCall(
        () => submitResponseFn(response),
        'submitResponse',
        { maxAttempts: 2 } // Only retry once for submissions to avoid duplicate submissions
      );

      if (!result.data.success || !result.data.data) {
        throw new Error(result.data.error || 'Failed to submit response');
      }

      return result.data.data; // Response ID
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to submit response';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get all responses for a survey
   */
  const getResponses = useCallback(async (surveyId: string): Promise<DynamicSurveyResponse[]> => {
    setLoading(true);
    setError(null);

    try {
      const getResponsesFn = httpsCallable<{ surveyId: string }, APIResponse<DynamicSurveyResponse[]>>(
        functions,
        'getResponses'
      );

      const result = await retryFirebaseCall(
        () => getResponsesFn({ surveyId }),
        'getResponses'
      );

      if (!result.data.success || !result.data.data) {
        throw new Error(result.data.error || 'Failed to get responses');
      }

      return result.data.data;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to get responses';
      console.error('[useResponses] Error getting responses:', err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    submitResponse,
    getResponses,
  };
};
